import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CalendarCheck, Star, MapPin } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, BOOKING_URL, IMAGES } from "../data/site";
import { useRatingFigures } from "./LiveRatingProvider";

const line = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.75, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { rating, count } = useRatingFigures();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const chipY = useTransform(scrollYProgress, [0, 1], [0, -30]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-[72px]"
    >
      {/* ambient glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, rgba(234,88,12,0.35) 0%, transparent 65%)" }}
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-14 sm:pt-20 lg:pt-24 pb-16 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Type column */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.28em] text-orange-500 font-semibold mb-6 flex items-center gap-2"
          >
            <MapPin size={13} className="text-orange-500" />
            SWS Auto Electrics — Sunshine Coast
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-display font-bold uppercase text-slate-50 leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
          >
            {[
              "Auto electrics &",
              "air conditioning,",
              <span key="l3" className="text-orange-600">we come to you.</span>,
            ].map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            Mobile auto electrical and air conditioning work across the Sunshine
            Coast — batteries, alternators, starters, diagnostics, towing setups
            and aircon, done at your home, your work or your camp.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={PHONE_TEL}
              data-testid="hero-call-cta"
              className="inline-flex items-center justify-center gap-2.5 h-14 px-8 rounded-full bg-orange-600 hover:bg-orange-500 text-ink font-bold text-base transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(234,88,12,0.35)]"
            >
              <Phone size={19} strokeWidth={2.5} />
              Call Sam — {PHONE_DISPLAY}
            </a>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-cta"
              className="inline-flex items-center justify-center gap-2.5 h-14 px-8 rounded-full border border-line bg-surface/60 hover:border-orange-600/60 hover:text-orange-400 text-slate-200 font-semibold text-base backdrop-blur transition-all hover:-translate-y-0.5"
            >
              <CalendarCheck size={18} />
              Book Now
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400"
          >
            <span data-testid="hero-badge-rating" className="inline-flex items-center gap-2">
              <Star size={14} className="fill-orange-500 text-orange-500" />
              {rating} from {count} Google reviews
            </span>
            <span data-testid="hero-badge-years" className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-600 inline-block" />
              8 years on the road
            </span>
            <span data-testid="hero-badge-mobile" className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-600 inline-block" />
              Fully mobile
            </span>
          </motion.div>
        </div>

        {/* Image column */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ y: imgY }}
            className="relative rounded-2xl border border-line bg-surface overflow-hidden"
          >
            <img
              src={IMAGES.heroSam}
              alt="Sam from SWS Auto Electrics at the open rear of the mobile workshop ute"
              className="w-full h-[340px] sm:h-[420px] lg:h-[480px] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-ink/70 to-transparent" />
            <p className="absolute top-4 left-5 right-5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
              The workshop comes to you — Sunshine Coast, QLD
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            style={{ y: chipY }}
            className="absolute -bottom-6 -left-4 sm:-left-8 rounded-xl border border-line bg-[rgba(18,24,36,0.85)] backdrop-blur-xl px-5 py-4 shadow-[0_16px_50px_rgba(0,0,0,0.45)]"
          >
            <p
              data-testid="hero-stat-rating"
              className="font-display font-bold text-3xl text-orange-500 leading-none flex items-center gap-2"
            >
              {rating}
              <span className="flex gap-0.5" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} className="fill-orange-500 text-orange-500" />
                ))}
              </span>
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-2">
              {count} Google reviews
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
